import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import { HomePage } from "@/pages/HomePage";
import { LoginPage } from "@/pages/LoginPage";
import { SignUpPage } from "@/pages/SignUpPage";
import { AboutPage } from "@/pages/AboutPage";
import { ContactPage } from "@/pages/ContactPage";
import { TermsPage } from "@/pages/TermsPage";
import { PrivacyPage } from "@/pages/PrivacyPage";
import { CookiePolicyPage } from "@/pages/CookiePolicyPage";
import { SecurityPage } from "@/pages/SecurityPage";
import ChatwootSidebarPage from "@/pages/ChatwootSidebarPage";
import { DashboardLayout } from "@/components/dashboard/layout/DashboardLayout";
import { DashboardOverview } from "@/components/dashboard/overview/DashboardOverview";
import { FlowConfigurator } from "@/components/dashboard/FlowConfigurator";
import { MarketingPage } from "@/components/dashboard/marketing/MarketingPage";
import { TemplatesPage } from "@/components/dashboard/templates/TemplatesPage";
import { SequencesPage } from "@/components/dashboard/sequences/SequencesPage";
import { CampaignsPage } from "@/components/dashboard/campaigns/CampaignsPage";
import { RoutingPage } from "@/components/dashboard/routing/RoutingPage";
import { FlowGraphsListPage } from "@/components/dashboard/flow-builder/FlowGraphsListPage";
import { FlowEditorPage } from "@/components/dashboard/flow-builder/FlowEditorPage";
import { FlowRunsPage } from "@/components/dashboard/flow-builder/FlowRunsPage";
import { SegmentsPage } from "@/components/dashboard/segments/SegmentsPage";
import { AnalyticsPage } from "@/components/dashboard/analytics/AnalyticsPage";
import { QueueMonitorPage } from "@/components/dashboard/queue/QueueMonitorPage";
import { WhatsAppLogsPage } from "@/components/dashboard/logs/WhatsAppLogsPage";
import { BillingSettings } from "@/components/dashboard/billing/BillingSettings";
import { ProfileSettings } from "@/components/dashboard/settings/ProfileSettings";
import { PricingPage } from "@/pages/PricingPage";
import { UnsubscribePage } from "@/pages/UnsubscribePage";
import { DataDeletionPage } from "@/pages/DataDeletionPage";
import { DeletionStatusPage } from "@/pages/DeletionStatusPage";
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";

// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

// Google Analytics page tracking
function AnalyticsTracker() {
  const location = useLocation();

  useEffect(() => {
    // Track page views with GA4
    if (window.gtag) {
      window.gtag('event', 'page_view', {
        page_path: location.pathname + location.search,
        page_title: document.title
      });
    }
  }, [location]);

  return null;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <ScrollToTop />
          <AnalyticsTracker />
          <Toaster richColors closeButton position="top-right" />
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<SignUpPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/cookies" element={<CookiePolicyPage />} />
            <Route path="/security" element={<SecurityPage />} />
            <Route path="/chatwoot-sidebar" element={<ChatwootSidebarPage />} />
            <Route path="/dashboard" element={<DashboardLayout />}>
              <Route index element={<DashboardOverview />} />
              <Route path="flows" element={<FlowConfigurator />} />
              <Route path="flow-builder" element={<FlowGraphsListPage />} />
              <Route path="flow-builder/:graphId" element={<FlowEditorPage />} />
              <Route path="flow-builder/:graphId/runs" element={<FlowRunsPage />} />
              <Route path="templates" element={<TemplatesPage />} />
              <Route path="sequences" element={<SequencesPage />} />
              <Route path="campaigns" element={<CampaignsPage />} />
              <Route path="segments" element={<SegmentsPage />} />
              <Route path="analytics" element={<AnalyticsPage />} />
              <Route path="queue" element={<QueueMonitorPage />} />
              <Route path="whatsapp-logs" element={<WhatsAppLogsPage />} />
              <Route path="routing" element={<RoutingPage />} />
              <Route path="marketing" element={<MarketingPage />} />
              <Route path="billing" element={<BillingSettings />} />
              <Route path="profile" element={<ProfileSettings />} />
            </Route>
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/unsubscribe" element={<UnsubscribePage />} />
            <Route path="/data-deletion" element={<DataDeletionPage />} />
            <Route path="/deletion-status" element={<DeletionStatusPage />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
